import { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { islamiyahAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon, PencilIcon, XMarkIcon, AcademicCapIcon, UserIcon } from '@heroicons/react/24/outline';

// ── Field wrapper ─────────────────────────────────────────────
const Field = ({ label, hint, children }) => (
  <div>
    <label className="form-label">
      {label}
      {hint && <span className="text-gray-400 font-normal text-xs ml-2">{hint}</span>}
    </label>
    {children}
  </div>
);

// ══════════════════════════════════════════════════════════════
// ADMIN ISLAMIYAH PAGE
// ══════════════════════════════════════════════════════════════
export default function AdminIslamiyahPage() {
  const [activeTab, setActiveTab] = useState('info');

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-navy-800">JNF Islamiyah</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-gray-100 mb-6">
        {[
          { key: 'info',     label: 'School Info'  },
          { key: 'branches', label: 'Branches'      },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition-all -mb-px ${
              activeTab === key
                ? 'border-cyan-400 text-navy-800 bg-white'
                : 'border-transparent text-gray-400 hover:text-navy-600'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'info'     && <IslamiyahInfoTab />}
      {activeTab === 'branches' && <IslamiyahBranchesTab />}
    </AdminLayout>
  );
}

// ── TAB 1: School Info ────────────────────────────────────────
function IslamiyahInfoTab() {
  const DEFAULTS = {
    school_name: 'JNF Islamiyah', tagline: '', about: '',
    history: '', founded_year: '', cover_url: '', phone: '', email: '',
  };
  const [form, setForm]     = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    islamiyahAPI.getInfo().then(r => {
      const d = r.data.data;
      if (d) setForm({ ...DEFAULTS, ...d,
        school_name:  d.school_name  || 'JNF Islamiyah',
        tagline:      d.tagline      || '',
        about:        d.about        || '',
        history:      d.history      || '',
        founded_year: d.founded_year || '',
        cover_url:    d.cover_url    || '',
        phone:        d.phone        || '',
        email:        d.email        || '',
      });
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await islamiyahAPI.updateInfo(form);
      toast.success('School info updated!');
    } catch { toast.error('Failed to save school info.'); }
    finally { setSaving(false); }
  };

  if (!loaded) return <div className="h-40 bg-gray-100 rounded-2xl animate-pulse" />;

  return (
    <form onSubmit={save} className="max-w-2xl bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">

      <Field label="School Name *">
        <input required className="form-input" value={form.school_name} onChange={e => set('school_name', e.target.value)} />
      </Field>

      <Field label="Tagline" hint="(short one-line description shown under the name)">
        <input className="form-input" value={form.tagline} onChange={e => set('tagline', e.target.value)}
          placeholder="e.g. Nurturing young minds in the light of Islam" />
      </Field>

      <Field label="Cover / Banner Image URL" hint="(hero photo at top of public page)">
        <input className="form-input" value={form.cover_url} onChange={e => set('cover_url', e.target.value)}
          placeholder="https://..." />
        {form.cover_url && (
          <img src={form.cover_url} alt="preview" className="mt-2 h-28 w-full object-cover rounded-xl border border-gray-100" />
        )}
      </Field>

      <Field label="About" hint="(overview shown on the About tab)">
        <textarea rows={5} className="form-input resize-none" value={form.about}
          onChange={e => set('about', e.target.value)}
          placeholder="What is JNF Islamiyah? What is its mission and purpose?..." />
      </Field>

      <Field label="History" hint="(how it started — shown below About)">
        <textarea rows={5} className="form-input resize-none" value={form.history}
          onChange={e => set('history', e.target.value)}
          placeholder="When and how was JNF Islamiyah founded? How has it grown?..." />
      </Field>

      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Year Founded">
          <input type="number" min="1900" max="2100" className="form-input" value={form.founded_year}
            onChange={e => set('founded_year', e.target.value)} placeholder="e.g. 1995" />
        </Field>
        <Field label="Phone">
          <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)}
            placeholder="+234 800 000 0000" />
        </Field>
        <Field label="Email">
          <input type="email" className="form-input" value={form.email} onChange={e => set('email', e.target.value)}
            placeholder="islamiyah@jnf.org" />
        </Field>
      </div>

      <button type="submit" disabled={saving} className="btn-primary text-sm py-2.5 px-6 flex items-center gap-2">
        {saving && <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
        {saving ? 'Saving...' : 'Save School Info'}
      </button>
    </form>
  );
}

// ── TAB 2: Branches ───────────────────────────────────────────
const EMPTY_BRANCH = {
  name: '', name_ar: '', description: '', address: '',
  city: '', state: '', country: 'Nigeria',
  phone: '', email: '', cover_url: '', sort_order: 0, is_published: true,
};

function IslamiyahBranchesTab() {
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState(EMPTY_BRANCH);
  const [saving, setSaving]     = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const fetchBranches = () =>
    islamiyahAPI.getBranchesAdmin().then(r => setBranches(r.data.data || []));
  useEffect(() => { fetchBranches(); }, []);

  const openCreate = () => {
    setEditId(null);
    setForm({ ...EMPTY_BRANCH, sort_order: branches.length });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEdit = (b) => {
    setEditId(b.id);
    setForm({
      name:        b.name        || '',
      name_ar:     b.name_ar     || '',
      description: b.description || '',
      address:     b.address     || '',
      city:        b.city        || '',
      state:       b.state       || '',
      country:     b.country     || 'Nigeria',
      phone:       b.phone       || '',
      email:       b.email       || '',
      cover_url:   b.cover_url   || '',
      sort_order:  b.sort_order  ?? 0,
      is_published: !!b.is_published,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeForm = () => { setShowForm(false); setEditId(null); setForm(EMPTY_BRANCH); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Branch name is required.'); return; }
    setSaving(true);
    try {
      if (editId) {
        await islamiyahAPI.updateBranch(editId, form);
        toast.success('Branch updated!');
      } else {
        await islamiyahAPI.createBranch(form);
        toast.success('Branch added!');
      }
      closeForm(); fetchBranches();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save branch.');
    } finally { setSaving(false); }
  };

  const togglePublished = async (b) => {
    try {
      await islamiyahAPI.updateBranch(b.id, { is_published: b.is_published ? 0 : 1 });
      toast.success(b.is_published ? 'Branch hidden.' : 'Branch published!');
      fetchBranches();
    } catch { toast.error('Failed to update.'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this branch? This cannot be undone.')) return;
    try {
      await islamiyahAPI.deleteBranch(id);
      toast.success('Branch deleted.');
      fetchBranches();
    } catch { toast.error('Failed to delete branch.'); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-400">{branches.length} branch{branches.length !== 1 ? 'es' : ''}</p>
        <button onClick={openCreate} className="btn-primary text-sm py-2 px-4 flex items-center gap-1">
          <PlusIcon className="h-4 w-4" /> Add Branch
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-navy-700">{editId ? 'Edit Branch' : 'New Branch'}</h2>
            <button type="button" onClick={closeForm} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Name */}
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Branch Name *">
              <input required className="form-input" value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="e.g. Tanke Branch, GRA Branch" />
            </Field>
            <Field label="Arabic Name" hint="(optional)">
              <input className="form-input" value={form.name_ar}
                onChange={e => set('name_ar', e.target.value)}
                style={{ fontFamily: "'Amiri',serif", direction: 'rtl' }}
                placeholder="الاسم بالعربية" dir="rtl" />
            </Field>
          </div>

          {/* Photo */}
          <Field label="Branch Photo URL" hint="(photo of the school building or classroom)">
            <input className="form-input" value={form.cover_url}
              onChange={e => set('cover_url', e.target.value)} placeholder="https://..." />
            {form.cover_url && (
              <img src={form.cover_url} alt="preview" className="mt-2 h-36 w-full object-cover rounded-xl border border-gray-100" />
            )}
          </Field>

          {/* Description */}
          <Field label="Description">
            <textarea rows={3} className="form-input resize-none" value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Brief description of this branch — classes offered, age groups, etc." />
          </Field>

          {/* Address */}
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Address">
              <input className="form-input" value={form.address}
                onChange={e => set('address', e.target.value)}
                placeholder="Street address or landmark" />
            </Field>
            <Field label="City">
              <input className="form-input" value={form.city}
                onChange={e => set('city', e.target.value)}
                placeholder="e.g. Ilorin" />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="State">
              <input className="form-input" value={form.state}
                onChange={e => set('state', e.target.value)}
                placeholder="e.g. Kwara State" />
            </Field>
            <Field label="Country">
              <input className="form-input" value={form.country}
                onChange={e => set('country', e.target.value)} />
            </Field>
          </div>

          {/* Contact */}
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Phone">
              <input className="form-input" value={form.phone}
                onChange={e => set('phone', e.target.value)} placeholder="+234 800 000 0000" />
            </Field>
            <Field label="Email">
              <input type="email" className="form-input" value={form.email}
                onChange={e => set('email', e.target.value)} placeholder="branch@jnf.org" />
            </Field>
          </div>

          {/* Sort + published */}
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Sort Order" hint="(lower = appears first)">
              <input type="number" min={0} className="form-input" value={form.sort_order}
                onChange={e => set('sort_order', parseInt(e.target.value) || 0)} />
            </Field>
            <div className="flex items-center gap-3 mt-6">
              <input type="checkbox" id="pub" checked={form.is_published}
                onChange={e => set('is_published', e.target.checked)}
                className="h-4 w-4 accent-cyan-500" />
              <label htmlFor="pub" className="text-sm text-navy-700">Published (visible on website)</label>
            </div>
          </div>

          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="btn-primary text-sm py-2 px-5 flex items-center gap-2">
              {saving && <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {saving ? 'Saving...' : editId ? 'Update Branch' : 'Save Branch'}
            </button>
            <button type="button" onClick={closeForm} className="btn-outline text-sm py-2 px-5">Cancel</button>
          </div>
        </form>
      )}

      {/* Branch cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {branches.map(b => (
          <div key={b.id} className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${b.is_published ? 'border-gray-100' : 'border-gray-200 opacity-60'}`}>

            {/* Cover */}
            <div className="h-36 bg-navy-100 relative overflow-hidden">
              {b.cover_url
                ? <img src={b.cover_url} alt={b.name} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy-700 to-navy-900">
                    <AcademicCapIcon className="h-12 w-12 text-white/20" />
                  </div>
              }
              {!b.is_published && (
                <div className="absolute top-2 left-2 bg-gray-800/70 text-white text-xs font-semibold px-2 py-0.5 rounded-full">Hidden</div>
              )}
            </div>

            <div className="p-4">
              {/* Branch info */}
              <p className="font-semibold text-navy-800 text-sm">{b.name}</p>
              {b.name_ar && <p className="arabic text-navy-500 text-xs mt-0.5">{b.name_ar}</p>}
              {b.description && <p className="text-gray-400 text-xs mt-1 line-clamp-2">{b.description}</p>}
              {b.address && <p className="text-gray-400 text-xs mt-1">📍 {[b.address, b.city].filter(Boolean).join(', ')}</p>}
              {b.phone && <p className="text-gray-400 text-xs">📞 {b.phone}</p>}

              {/* ── Teacher manager ── */}
              <TeacherManager branch={b} onUpdated={fetchBranches} />

              {/* Branch action buttons */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                <button onClick={() => togglePublished(b)}
                  className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                    b.is_published
                      ? 'bg-green-50 text-green-600 hover:bg-green-100'
                      : 'bg-gray-100 text-gray-500 hover:bg-green-50 hover:text-green-600'
                  }`}>
                  {b.is_published ? '✓ Published' : 'Publish'}
                </button>
                <button onClick={() => openEdit(b)} className="p-1.5 text-gray-300 hover:text-navy-500 hover:bg-navy-50 rounded-lg transition-colors" title="Edit branch">
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button onClick={() => del(b.id)} className="p-1.5 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg transition-colors ml-auto" title="Delete branch">
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {branches.length === 0 && (
          <div className="col-span-full text-center py-16 text-gray-400">
            <AcademicCapIcon className="h-12 w-12 mx-auto mb-3 text-gray-200" />
            <p className="mb-3">No branches added yet.</p>
            <button onClick={openCreate} className="btn-primary text-sm py-2 px-5 inline-flex items-center gap-1">
              <PlusIcon className="h-4 w-4" /> Add First Branch
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════
// TEACHER MANAGER — inline per branch card
// ══════════════════════════════════════════════════════════════
function TeacherManager({ branch, onUpdated }) {
  const [teachers, setTeachers]   = useState(branch.teachers || []);
  const [input, setInput]         = useState('');
  const [editId, setEditId]       = useState(null);
  const [editName, setEditName]   = useState('');
  const [saving, setSaving]       = useState(false);
  const inputRef = useRef();

  // Keep in sync if parent re-fetches
  useEffect(() => { setTeachers(branch.teachers || []); }, [branch.teachers]);

  const addTeacher = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setSaving(true);
    try {
      const res = await islamiyahAPI.createTeacher(branch.id, { name: input.trim() });
      setTeachers(t => [...t, { id: res.data.data.id, name: res.data.data.name }]);
      setInput('');
      toast.success('Teacher added!');
      onUpdated();
    } catch { toast.error('Failed to add teacher.'); }
    finally { setSaving(false); }
  };

  const startEdit = (t) => { setEditId(t.id); setEditName(t.name); };
  const cancelEdit = () => { setEditId(null); setEditName(''); };

  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    try {
      await islamiyahAPI.updateTeacher(id, { name: editName.trim() });
      setTeachers(ts => ts.map(t => t.id === id ? { ...t, name: editName.trim() } : t));
      toast.success('Teacher updated.');
      cancelEdit();
      onUpdated();
    } catch { toast.error('Failed to update teacher.'); }
  };

  const removeTeacher = async (id) => {
    try {
      await islamiyahAPI.deleteTeacher(id);
      setTeachers(ts => ts.filter(t => t.id !== id));
      toast.success('Teacher removed.');
      onUpdated();
    } catch { toast.error('Failed to remove teacher.'); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">

      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-navy-600 flex items-center gap-1.5">
          <UserIcon className="h-3.5 w-3.5 text-cyan-500" />
          Teaching Staff
          <span className="text-gray-400 font-normal">({teachers.length})</span>
        </p>
      </div>

      {/* Teacher list */}
      {teachers.length > 0 && (
        <ul className="space-y-1 mb-3">
          {teachers.map(t => (
            <li key={t.id} className="flex items-center gap-2 group">
              {editId === t.id ? (
                <>
                  <input
                    autoFocus
                    className="form-input py-1 text-xs flex-1"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(t.id); if (e.key === 'Escape') cancelEdit(); }}
                  />
                  <button onClick={() => saveEdit(t.id)} className="text-xs text-green-600 font-semibold hover:text-green-700 flex-shrink-0">Save</button>
                  <button onClick={cancelEdit} className="text-xs text-gray-400 hover:text-gray-600 flex-shrink-0">Cancel</button>
                </>
              ) : (
                <>
                  <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
                  <span className="text-sm text-navy-700 flex-1 min-w-0 truncate">{t.name}</span>
                  <button
                    onClick={() => startEdit(t)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-300 hover:text-navy-500 transition-all flex-shrink-0"
                    title="Rename"
                  >
                    <PencilIcon className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => removeTeacher(t.id)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-300 hover:text-red-400 transition-all flex-shrink-0"
                    title="Remove"
                  >
                    <TrashIcon className="h-3 w-3" />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Add teacher input */}
      <form onSubmit={addTeacher} className="flex gap-2">
        <input
          ref={inputRef}
          className="form-input py-1.5 text-sm flex-1"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Teacher name..."
        />
        <button
          type="submit"
          disabled={saving || !input.trim()}
          className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1 flex-shrink-0 disabled:opacity-40"
        >
          {saving
            ? <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <PlusIcon className="h-3.5 w-3.5" />
          }
          Add
        </button>
      </form>
    </div>
  );
}
